import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Wallet, Loader2, FileText, Receipt, AlertTriangle, ExternalLink,
  DollarSign, Ban, Download,
} from "lucide-react";
import { descargarFacturaPDF } from "@/features/config/api";
import { openBlobInNewTab } from "@/lib/pdf";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate, formatDateTime, formatMXN } from "@/lib/utils";
import {
  useAnularFactura, useFactura, usePagosFactura,
  useEstadoCuenta, useAplicarSaldoFavor,
} from "./api";
import { useCliente } from "@/features/clientes/api";
import { useContrato } from "@/features/contratos/api";
import { useBackLink } from "@/hooks/useBackLink";
import { ESTATUS_LABEL, ESTATUS_VARIANT, METODO_LABEL } from "./helpers";
import { AplicarPagoDialog } from "./AplicarPagoDialog";
import { ActionDialog } from "@/components/ActionDialog";
import { useAuthStore } from "@/lib/auth";
import type { ApiError } from "@/lib/api";

export function FacturaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackLink("/facturas", "Facturas");
  const { data: factura, isLoading, isError } = useFactura(id ?? null);
  const { data: cliente } = useCliente(factura?.cliente_id ?? null);
  const { data: contrato } = useContrato(factura?.contrato_id ?? null);
  const { data: pagosPaged } = usePagosFactura(id ?? null);
  const { data: estado } = useEstadoCuenta(factura?.cliente_id ?? null);
  const pagos = pagosPaged?.items ?? [];
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [anularDialogOpen, setAnularDialogOpen] = useState(false);
  const [saldoFavorDialogOpen, setSaldoFavorDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const anularMut = useAnularFactura();
  const aplicarSaldoMut = useAplicarSaldoFavor();

  // rc34: cajero registra pagos pero NO anula facturas (puede ocultar adeudos).
  // pagos.write basta para mostrar "Registrar pago"; facturas.anular gates "Anular".
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRegistrarPago = hasPermission("pagos.write");
  const canAnularFactura = hasPermission("facturas.anular");

  async function handleDescargarPDF(f: typeof factura) {
    if (!f) return;
    setDownloadingPdf(true);
    try {
      const blob = await descargarFacturaPDF(f.id);
      openBlobInNewTab(blob);
    } catch (e) {
      const msg = (e as { message?: string }).message ?? "Ver logs del API";
      toast.error("No se pudo generar el PDF", { description: msg });
    } finally {
      setDownloadingPdf(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (isError || !factura) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <Wallet size={32} className="text-muted-foreground" />
        <h2 className="text-base font-semibold">Factura no encontrada</h2>
        <Button size="sm" variant="outline" onClick={() => navigate(back.path)}>
          <ArrowLeft size={14} /> Volver a {back.label}
        </Button>
      </div>
    );
  }

  const saldo = Number(factura.saldo);
  const pagado = Number(factura.total) - saldo;
  const pagosActivos = pagos.filter((p) => !p.anulado);
  // rc88 — Si algún pago activo es de Toku, la factura NO se puede anular
  // (Toku no revierte el cobro). El backend ya lo bloquea; aquí evitamos abrir
  // el diálogo y avisamos de una vez.
  const facturaTieneToku = pagosActivos.some(
    (p) => (p.payment_provider || "").toLowerCase() === "toku",
  );
  // rc68 — saldo a favor disponible del cliente (pagos huérfanos no aplicados).
  const saldoAFavor = Number(estado?.saldo_a_favor ?? 0);
  const puedeAplicarSaldoFavor =
    factura.estatus !== "anulada" && saldo > 0 && canRegistrarPago && saldoAFavor > 0;
  const saldoFavorAplicable = Math.min(saldoAFavor, saldo);

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumbs + header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Link
            to={back.path}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-fit"
          >
            <ArrowLeft size={12} />
            {back.label}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold font-mono">{factura.folio}</h1>
            <Badge variant={ESTATUS_VARIANT[factura.estatus]}>
              {ESTATUS_LABEL[factura.estatus]}
            </Badge>
            <Badge variant="outline">{factura.moneda}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>Emitida: {formatDate(factura.fecha_emision)}</span>
            <span>·</span>
            <span>Vence: {formatDate(factura.fecha_vencimiento)}</span>
            {factura.fecha_pago && (
              <>
                <span>·</span>
                <span>Pagada: {formatDate(factura.fecha_pago)}</span>
              </>
            )}
          </div>
        </div>
        {/* Acciones — PDF (F20.5) + Registrar pago (F20.3) + Anular (F20.4) */}
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => handleDescargarPDF(factura)}
            disabled={downloadingPdf}
          >
            <Download size={12} />
            {downloadingPdf ? "Generando…" : "Descargar PDF"}
          </Button>
          {factura.estatus !== "anulada" && (
            <>
              {puedeAplicarSaldoFavor && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSaldoFavorDialogOpen(true)}
                  className="border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
                >
                  <Wallet size={12} /> Pagar con saldo a favor
                </Button>
              )}
              {saldo > 0 && canRegistrarPago && (
                <Button
                  size="sm"
                  onClick={() => setPagoDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <DollarSign size={12} /> Registrar pago
                </Button>
              )}
              {canAnularFactura && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    if (facturaTieneToku) {
                      toast.error("No se puede anular", {
                        description:
                          "Esta factura tiene un pago registrado mediante Toku. Un cobro ya procesado por Toku no puede revertirse.",
                      });
                      return;
                    }
                    setAnularDialogOpen(true);
                  }}
                  title={
                    facturaTieneToku
                      ? "No se puede anular: tiene pago de Toku"
                      : undefined
                  }
                >
                  <Ban size={12} /> Anular
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Aviso de anulación */}
      {factura.estatus === "anulada" && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-destructive mt-0.5 shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="font-medium text-destructive">Factura anulada</p>
              {factura.motivo_anulacion && (
                <p className="text-destructive/80">
                  <span className="font-medium">Motivo: </span>
                  {factura.motivo_anulacion}
                </p>
              )}
              {factura.anulada_at && (
                <p className="text-[10px] text-muted-foreground">
                  {formatDateTime(factura.anulada_at)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cliente / Contrato meta */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Meta
          icon={Receipt}
          title="Cliente"
          body={
            cliente ? (
              <Link
                to={`/clientes/${cliente.id}`}
                className="inline-flex items-center gap-1 hover:underline"
              >
                <span className="font-mono">{cliente.codigo}</span>
                <span>·</span>
                <span>{cliente.razon_social}</span>
                <ExternalLink size={10} className="opacity-50" />
              </Link>
            ) : (
              <span className="text-muted-foreground">
                {factura.cliente_id.slice(0, 8)}…
              </span>
            )
          }
        />
        {factura.contrato_id && (
          <Meta
            icon={FileText}
            title="Contrato"
            body={
              contrato ? (
                <Link
                  to={`/contratos/${contrato.id}`}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <span className="font-mono">{contrato.codigo}</span>
                  <ExternalLink size={10} className="opacity-50" />
                </Link>
              ) : (
                <span className="text-muted-foreground">
                  {factura.contrato_id.slice(0, 8)}…
                </span>
              )
            }
          />
        )}
      </div>

      {/* Totales */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <TotalCard label="Subtotal" value={formatMXN(factura.subtotal)} />
        <TotalCard
          label={`Descuento ${Number(factura.descuento_pct) > 0 ? `(${factura.descuento_pct}%)` : ""}`}
          value={formatMXN(factura.descuento_monto)}
          muted={Number(factura.descuento_monto) === 0}
        />
        <TotalCard label="Total" value={formatMXN(factura.total)} emphasis />
        <TotalCard
          label="Saldo"
          value={formatMXN(saldo)}
          variant={
            saldo > 0 && factura.estatus !== "anulada"
              ? "destructive"
              : "success"
          }
        />
      </div>

      {/* Items */}
      <section>
        <h3 className="mb-2 text-sm font-semibold">Conceptos</h3>
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Concepto</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead className="text-right">Precio unitario</TableHead>
                <TableHead className="text-right">Importe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {factura.items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium">{it.concepto}</span>
                      {it.descripcion && (
                        <span className="text-[11px] text-muted-foreground">
                          {it.descripcion}
                        </span>
                      )}
                      {(it.cfdi_clave_prod_serv || it.cfdi_clave_unidad) && (
                        <span className="text-[10px] font-mono text-muted-foreground">
                          CFDI: {it.cfdi_clave_prod_serv} / {it.cfdi_clave_unidad}
                          {it.cfdi_objeto_imp && ` / obj=${it.cfdi_objeto_imp}`}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{it.cantidad}</TableCell>
                  <TableCell className="text-xs">{it.unidad}</TableCell>
                  <TableCell className="text-right">
                    {formatMXN(it.precio_unitario)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMXN(it.importe)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Pagos aplicados */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Pagos aplicados</h3>
          <span className="text-xs text-muted-foreground">
            {pagosActivos.length} pago{pagosActivos.length === 1 ? "" : "s"} · {formatMXN(pagado)} aplicado
          </span>
        </div>
        <div className="rounded-lg border bg-card">
          {pagos.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              Sin pagos aplicados todavía.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Folio</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagos.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.folio}</TableCell>
                    <TableCell className="text-xs">{formatDate(p.fecha)}</TableCell>
                    <TableCell className="text-xs">{METODO_LABEL[p.metodo]}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.referencia ?? "—"}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMXN(p.monto)}
                    </TableCell>
                    <TableCell>
                      {p.anulado ? (
                        <Badge variant="secondary">Anulado</Badge>
                      ) : (
                        <Badge variant="success">Aplicado</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      {/* CFDI (si está timbrada) */}
      {(factura.cfdi_uuid || factura.cfdi_provider) && (
        <section>
          <h3 className="mb-2 text-sm font-semibold">CFDI</h3>
          <div className="rounded-lg border bg-card p-3">
            <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
              <Meta
                title="UUID"
                body={
                  <span className="font-mono text-[11px]">
                    {factura.cfdi_uuid ?? "—"}
                  </span>
                }
              />
              <Meta title="Proveedor" body={factura.cfdi_provider ?? "—"} />
              <Meta title="Status CFDI" body={factura.cfdi_status ?? "—"} />
              <Meta
                title="Método de pago"
                body={factura.cfdi_metodo_pago ?? "—"}
              />
              {factura.cfdi_folio_sat && (
                <Meta title="Folio SAT" body={factura.cfdi_folio_sat} />
              )}
              {factura.cfdi_xml_url && (
                <Meta
                  title="XML"
                  body={
                    <a
                      href={factura.cfdi_xml_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Descargar
                    </a>
                  }
                />
              )}
              {factura.cfdi_pdf_url && (
                <Meta
                  title="PDF CFDI"
                  body={
                    <a
                      href={factura.cfdi_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Descargar
                    </a>
                  }
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Notas internas */}
      {(factura.notas || factura.notas_internas) && (
        <section>
          <h3 className="mb-2 text-sm font-semibold">Notas</h3>
          <div className="rounded-lg border bg-card p-3 text-xs space-y-2">
            {factura.notas && (
              <div>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Notas (cliente)
                </span>
                <p className="mt-0.5">{factura.notas}</p>
              </div>
            )}
            {factura.notas_internas && (
              <div>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Notas internas
                </span>
                <p className="mt-0.5">{factura.notas_internas}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Diálogo F20.3 — aplicar pago */}
      <AplicarPagoDialog
        open={pagoDialogOpen}
        onOpenChange={setPagoDialogOpen}
        factura={factura}
      />

      {/* Diálogo rc68 — aplicar saldo a favor a esta factura */}
      <ActionDialog
        open={saldoFavorDialogOpen}
        onOpenChange={setSaldoFavorDialogOpen}
        title={`Aplicar saldo a favor a ${factura.folio}`}
        description={
          `El cliente tiene ${formatMXN(saldoAFavor)} de saldo a favor. Se aplicarán ` +
          `${formatMXN(saldoFavorAplicable)} a esta factura (saldo ${formatMXN(saldo)}), ` +
          `FIFO por fecha de pago. Queda registrado en auditoría. El motivo es opcional. ` +
          `Nota: esta aplicación NO se notifica a Toku — el crédito ya está reflejado de su lado, ` +
          `así se evita un pago duplicado.`
        }
        actionLabel="Aplicar saldo a favor"
        motivoRequired={false}
        isPending={aplicarSaldoMut.isPending}
        onConfirm={async (motivo) => {
          try {
            const res = await aplicarSaldoMut.mutateAsync({
              factura_id: factura.id,
              motivo: motivo ?? undefined,
            });
            toast.success(`Saldo a favor aplicado a ${factura.folio}`, {
              description: res.factura_quedo_pagada
                ? `Factura pagada · ${formatMXN(res.total_aplicado)} aplicado`
                : `${formatMXN(res.total_aplicado)} aplicado · saldo ${formatMXN(res.factura_saldo_despues ?? 0)}`,
            });
            setSaldoFavorDialogOpen(false);
          } catch (e) {
            const api = e as ApiError;
            return api.message ?? "No se pudo aplicar el saldo a favor";
          }
        }}
      />

      {/* Diálogo F20.4 — anular factura con motivo */}
      <ActionDialog
        open={anularDialogOpen}
        onOpenChange={setAnularDialogOpen}
        title={`Anular factura ${factura.folio}`}
        description={
          pagosActivos.length > 0
            ? `Se anularán también los ${pagosActivos.length} pago(s) aplicados (${formatMXN(pagado)} en total). Esta acción queda registrada en auditoría y no se puede deshacer.`
            : "Esta acción queda registrada en auditoría y no se puede deshacer."
        }
        actionLabel="Sí, anular factura"
        actionVariant="destructive"
        motivoRequired={true}
        isPending={anularMut.isPending}
        onConfirm={async (motivo) => {
          if (!motivo || motivo.trim().length < 10) {
            return "El motivo debe tener al menos 10 caracteres (sé específico para auditoría).";
          }
          try {
            await anularMut.mutateAsync({
              factura_id: factura.id,
              motivo: motivo.trim(),
            });
            toast.success(`Factura ${factura.folio} anulada`, {
              description: pagosActivos.length > 0
                ? `${pagosActivos.length} pago(s) anulados en cascada`
                : "Registrada en auditoría",
            });
            setAnularDialogOpen(false);
          } catch (e) {
            const api = e as ApiError;
            return api.message ?? "No se pudo anular la factura";
          }
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function Meta({
  icon: Icon, title, body,
}: {
  icon?: typeof Receipt;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon size={11} />}
        <span>{title}</span>
      </div>
      <div className="mt-1 text-sm">{body}</div>
    </div>
  );
}

function TotalCard({
  label, value, muted, emphasis, variant,
}: {
  label: string;
  value: string;
  muted?: boolean;
  emphasis?: boolean;
  variant?: "destructive" | "success";
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={[
          "leading-none",
          emphasis ? "text-lg font-semibold" : "text-base",
          muted ? "text-muted-foreground" : "",
          variant === "destructive" ? "text-destructive font-semibold" : "",
          variant === "success" ? "text-emerald-600 dark:text-emerald-400" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {value}
      </span>
    </div>
  );
}
